import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, MessageSquare, Calendar, UserPlus, UserCheck } from 'lucide-react';
import { formatDateShort } from '../utils/datetime';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15000;
export const NOTIF_REFRESH_EVENT = 'm4l:notifications-refresh';
const EMPTY = { total: 0, messages: [], events: [], pending_requests: [], accepted: [] };

const NotifItem = ({ to, unread, icon: Icon, testId, onClick, children }) => (
    <Link
        to={to}
        onClick={onClick}
        className={`block px-4 py-3 border-b border-[#E5E0D8]/60 transition-colors ${unread ? 'bg-[#5B2D8E]/10 hover:bg-[#5B2D8E]/15' : 'hover:bg-[#F9F6F0]'}`}
        data-testid={testId}
        data-unread={unread ? 'true' : 'false'}
    >
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 mt-0.5 text-[#5B2D8E] shrink-0" />
            <div className={`min-w-0 flex-1 ${unread ? 'font-semibold' : ''}`}>{children}</div>
            {unread && <span className="w-2 h-2 mt-1.5 rounded-full bg-[#D4AF37] shrink-0" data-testid={`${testId}-dot`} />}
        </div>
    </Link>
);

const Section = ({ title, children }) => (
    <div>
        <p className="px-4 pt-3 pb-1 text-xs uppercase tracking-widest text-[#5B2D8E]">{title}</p>
        {children}
    </div>
);

export default function NotificationBell() {
    const [data, setData] = useState(EMPTY);
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);
    const seen = useRef(new Set());
    const mountTime = useRef(Date.now());
    const navigate = useNavigate();

    const isFresh = (iso) => {
        const t = Date.parse(iso || '');
        return !Number.isNaN(t) && t > mountTime.current;
    };

    const toastOnce = (key, iso, fn) => {
        if (seen.current.has(key)) return;
        seen.current.add(key);
        if (isFresh(iso)) fn(key);
    };

    const load = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
            const payload = { ...EMPTY, ...(res.data || {}) };
            payload.messages.filter(m => m.unread).forEach(m => toastOnce(`m-${m.id}`, m.created_at, (k) =>
                toast(`New message from ${m.sender_name || 'a member'}`, { id: k,
                    description: (m.content || '').slice(0, 120),
                    duration: 8000,
                    action: { label: 'Open', onClick: () => navigate(`/connections?open=${m.connection_id}`) }
                })));
            payload.pending_requests.forEach(r => toastOnce(`r-${r.id}`, r.created_at, (k) =>
                toast(`${r.from_user_name || 'Someone'} wants to connect`, { id: k,
                    description: r.message ? `"${r.message.slice(0, 100)}"` : 'Tap Review to open the request.',
                    duration: 8000,
                    action: { label: 'Review', onClick: () => navigate('/connections') }
                })));
            payload.accepted.filter(a => a.unread).forEach(a => toastOnce(`a-${a.id}`, a.accepted_at, (k) =>
                toast(`${a.to_user_name || 'A member'} accepted your request`, { id: k,
                    duration: 8000,
                    action: { label: 'Message', onClick: () => navigate(`/connections?open=${a.id}`) }
                })));
            setData(payload);
        } catch (err) {
            // silent — likely auth error
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        window.addEventListener('focus', load);
        window.addEventListener(NOTIF_REFRESH_EVENT, load);
        return () => {
            clearInterval(t);
            window.removeEventListener('focus', load);
            window.removeEventListener(NOTIF_REFRESH_EVENT, load);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (open && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('touchstart', onClick);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('touchstart', onClick);
        };
    }, [open]);

    // Opening the bell only clears "new events"; messages & requests stay until acted on
    const markEventsSeen = async () => {
        try {
            await axios.post(`${API_URL}/api/notifications/seen`, {}, { withCredentials: true });
        } catch (err) { /* ignore */ }
    };

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        if (next && data.events.length > 0) markEventsSeen();
    };

    const close = () => setOpen(false);
    const total = data.total || 0;
    const fmt = (d) => d ? formatDateShort(d) : '';
    const isEmpty = !data.messages.length && !data.events.length && !data.pending_requests.length && !data.accepted.length;

    return (
        <div className="relative" ref={panelRef}>
            <button
                onClick={handleToggle}
                className="relative p-2 text-[#4A4A68] hover:text-[#5B2D8E] transition-colors"
                aria-label="Notifications"
                data-testid="notification-bell"
            >
                <Bell className="w-5 h-5" />
                {total > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-[#1A1A2E] text-[10px] font-bold flex items-center justify-center"
                        data-testid="notification-badge"
                    >
                        {total > 9 ? '9+' : total}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 bg-white border border-[#E5E0D8] shadow-xl rounded-sm overflow-hidden z-50"
                    data-testid="notification-panel"
                >
                    <div className="px-4 py-3 border-b border-[#E5E0D8] bg-[#F9F6F0] flex items-center justify-between">
                        <p className="font-serif text-lg text-[#1A1A2E]">Notifications</p>
                        <Link to="/connections" onClick={close} className="text-xs text-[#5B2D8E] uppercase tracking-wider" data-testid="notif-open-connections">
                            All connections
                        </Link>
                    </div>

                    <div className="max-h-[70vh] sm:max-h-96 overflow-y-auto">
                        {data.pending_requests.length > 0 && (
                            <Section title="Connection requests">
                                {data.pending_requests.map(r => (
                                    <NotifItem key={r.id} to="/connections" unread icon={UserPlus} testId={`notif-request-${r.id}`} onClick={close}>
                                        <p className="text-sm text-[#1A1A2E]"><strong>{r.from_user_name || 'Someone'}</strong> wants to connect</p>
                                        {r.message && <p className="text-xs text-[#4A4A68] mt-1 line-clamp-2 font-normal">"{r.message}"</p>}
                                        <p className="text-xs text-[#4A4A68] mt-1 font-normal">{fmt(r.created_at)}</p>
                                    </NotifItem>
                                ))}
                            </Section>
                        )}

                        {data.accepted.length > 0 && (
                            <Section title="Accepted requests">
                                {data.accepted.map(a => (
                                    <NotifItem key={a.id} to={`/connections?open=${a.id}`} unread={a.unread} icon={UserCheck} testId={`notif-accepted-${a.id}`} onClick={close}>
                                        <p className="text-sm text-[#1A1A2E]"><strong>{a.to_user_name || 'A member'}</strong> accepted your request</p>
                                        <p className="text-xs text-[#4A4A68] mt-1 font-normal">{fmt(a.accepted_at)}</p>
                                    </NotifItem>
                                ))}
                            </Section>
                        )}

                        {data.messages.length > 0 && (
                            <Section title="Messages">
                                {data.messages.map(m => (
                                    <NotifItem key={m.id} to={`/connections?open=${m.connection_id}`} unread={m.unread} icon={MessageSquare} testId={`notif-message-${m.id}`} onClick={close}>
                                        <p className="text-sm text-[#1A1A2E]"><strong>{m.sender_name || 'Member'}</strong></p>
                                        <p className="text-xs text-[#4A4A68] line-clamp-2">{m.content}</p>
                                        <p className="text-xs text-[#4A4A68] mt-1 font-normal">{fmt(m.created_at)}</p>
                                    </NotifItem>
                                ))}
                            </Section>
                        )}

                        {data.events.length > 0 && (
                            <Section title="New events">
                                {data.events.map(ev => (
                                    <NotifItem key={ev.id} to="/events" icon={Calendar} testId={`notif-event-${ev.id}`} onClick={close}>
                                        <p className="text-sm text-[#1A1A2E]"><strong>{ev.title}</strong></p>
                                        <p className="text-xs text-[#4A4A68]">{fmt(ev.date)}</p>
                                    </NotifItem>
                                ))}
                            </Section>
                        )}

                        {isEmpty && (
                            <div className="text-center py-10 px-4 text-[#4A4A68]" data-testid="notif-empty">
                                <Bell className="w-8 h-8 mx-auto mb-2 text-[#D8B4E2]" />
                                <p className="text-sm">You're all caught up.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
